import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Search, X, Check } from 'lucide-react';
import { Input } from '../../components/ui/input';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '../../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';

const extractContext = (text, term) => {
  if (!text) return '';
  
  const lowerText = text.toLowerCase();
  const lowerTerm = term.toLowerCase();
  const termIndex = lowerText.indexOf(lowerTerm);
  
  if (termIndex === -1) return text;
  
  const startPos = Math.max(0, termIndex - 30);
  const endPos = Math.min(text.length, termIndex + term.length + 30);
  
  let context = text.substring(startPos, endPos);
  
  // If we truncated the beginning, add ellipsis
  if (startPos > 0) {
    context = '...' + context;
  }
  
  // If we truncated the end, add ellipsis
  if (endPos < text.length) {
    context = context + '...';
  }
  
  return context;
};

// Generate predictive search results function moved outside
const generatePredictiveResults = (hazards, term) => {
  if (!term || term.length < 2) return [];
  
  const lowerTerm = term.toLowerCase();
  const results = [];
  
  // Search through all hazards
  hazards.forEach(hazard => {
    const matches = [];
    
    // Check hazard ID
    if (hazard.hazardId?.toLowerCase().includes(lowerTerm)) {
      matches.push({
        id: hazard.hazardId,
        property: 'ID',
        text: hazard.hazardId,
        context: hazard.hazardId
      });
    }
    
    // Check description
    if (hazard.description?.toLowerCase().includes(lowerTerm)) {
      matches.push({
        id: hazard.hazardId,
        property: 'description',
        text: hazard.description,
        context: extractContext(hazard.description, lowerTerm)
      });
    }
    
    // Check hazard type
    if (hazard.type?.toLowerCase().includes(lowerTerm)) {
      matches.push({
        id: hazard.hazardId,
        property: 'type',
        text: hazard.type,
        context: hazard.type
      });
    }
    
    // Check hazard source
    if (hazard.hazardSource?.toLowerCase().includes(lowerTerm)) {
      matches.push({
        id: hazard.hazardId,
        property: 'hazard source',
        text: hazard.hazardSource,
        context: extractContext(hazard.hazardSource, lowerTerm)
      });
    }
    
    // Check causes
    if (hazard.causes?.some(cause => cause.toLowerCase().includes(lowerTerm))) {
      hazard.causes.forEach((cause) => {
        if (cause.toLowerCase().includes(lowerTerm)) {
          matches.push({
            id: hazard.hazardId,
            property: 'cause',
            text: cause,
            context: extractContext(cause, lowerTerm)
          });
        }
      });
    }
    
    // Check consequences
    if (hazard.consequences?.some(consequence => consequence.toLowerCase().includes(lowerTerm))) {
      hazard.consequences.forEach((consequence) => {
        if (consequence.toLowerCase().includes(lowerTerm)) {
          matches.push({
            id: hazard.hazardId,
            property: 'consequence',
            text: consequence,
            context: extractContext(consequence, lowerTerm)
          });
        }
      });
    }
    
    // Check mitigations
    if (hazard.mitigations?.some(mitigation => 
      mitigation.description?.toLowerCase().includes(lowerTerm) || 
      mitigation.safetyRequirement?.toLowerCase().includes(lowerTerm))) {
      
      hazard.mitigations.forEach((mitigation) => {
        if (mitigation.description?.toLowerCase().includes(lowerTerm)) {
          matches.push({
            id: hazard.hazardId,
            property: 'mitigation',
            text: mitigation.description,
            context: extractContext(mitigation.description, lowerTerm)
          });
        }
        
        if (mitigation.safetyRequirement?.toLowerCase().includes(lowerTerm)) {
          matches.push({
            id: hazard.hazardId,
            property: 'safety requirement',
            text: mitigation.safetyRequirement,
            context: extractContext(mitigation.safetyRequirement, lowerTerm)
          });
        }
      });
    }
    
    // Check SFAIRP statement
    if (hazard.sfairpStatement?.toLowerCase().includes(lowerTerm)) {
      matches.push({
        id: hazard.hazardId,
        property: 'SFAIRP statement',
        text: hazard.sfairpStatement,
        context: extractContext(hazard.sfairpStatement, lowerTerm)
      });
    }
    
    // Check residual risk description
    if (hazard.residualRiskDescription?.toLowerCase().includes(lowerTerm)) {
      matches.push({
        id: hazard.hazardId,
        property: 'residual risk',
        text: hazard.residualRiskDescription,
        context: extractContext(hazard.residualRiskDescription, lowerTerm)
      });
    }
    
    // Check project metadata fields
    const metadataFields = [
      { name: 'systems', property: 'system' },
      { name: 'operatingModes', property: 'operating mode' },
      { name: 'locations', property: 'location' },
      { name: 'projectLifecycles', property: 'lifecycle' },
      { name: 'disciplines', property: 'discipline' },
      { name: 'drawingPackages', property: 'drawing package' },
      { name: 'users', property: 'user' },
      { name: 'riskCategory', property: 'risk category' }
    ];
    
    metadataFields.forEach(({ name, property }) => {
      if (hazard[name]?.some(item => item.toLowerCase().includes(lowerTerm))) {
        hazard[name].forEach(item => {
          if (item.toLowerCase().includes(lowerTerm)) {
            matches.push({
              id: hazard.hazardId,
              property,
              text: item,
              context: item
            });
          }
        });
      }
    });
    
    // Add all matches from this hazard to the results
    results.push(...matches);
  });
  
  // Limit to max 10 results
  return results.slice(0, 10);
};

// Custom hook for search predictions
const useSearchPredictions = (searchTerm, hazards) => {
  const [predictions, setPredictions] = useState([]);
  const [showPredictions, setShowPredictions] = useState(false);
  
  // Use debounced search for better performance
  const debouncedSearch = useRef(null);
  
  useEffect(() => {
    if (debouncedSearch.current) {
      clearTimeout(debouncedSearch.current);
    }
    
    if (!searchTerm || searchTerm.length < 2) {
      setPredictions([]);
      setShowPredictions(false);
      return;
    }
    
    debouncedSearch.current = setTimeout(() => {
      const results = generatePredictiveResults(hazards, searchTerm);
      setPredictions(results);
      setShowPredictions(results.length > 0);
    }, 300); // 300ms debounce
    
    return () => {
      if (debouncedSearch.current) {
        clearTimeout(debouncedSearch.current);
      }
    };
  }, [searchTerm, hazards]);
  
  return { predictions, showPredictions, setShowPredictions };
};

// Separate component for Risk Badge
const RiskBadge = React.memo(({ rating, color }) => {
  // Determine text color based on background color brightness
  const isLight = color.match(/#(?:[0-9a-f]{3}){1,2}$/i) && 
    (parseInt(color.substring(1, 3), 16) * 0.299 + 
     parseInt(color.substring(3, 5), 16) * 0.587 + 
     parseInt(color.substring(5, 7), 16) * 0.114) > 128;
  
  return (
    <Badge 
      className="h-6" 
      style={{ 
        backgroundColor: color, 
        color: isLight ? '#000' : '#fff',
        border: '1px solid #ccc'
      }}
    >
      {rating}
    </Badge>
  );
});

// Main component
const HazardSearchFilter = ({ 
  riskMatrix,
  onFilterChange,
  activeFilters,
  onClearFilters,
  hazards = [],
  onHighlightResult = () => {}
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [openPopover, setOpenPopover] = useState({
    unmitigatedRisk: false,
    retainedRisk: false,
    riskStatus: false,
    mitigationStatus: false
  });
  const searchInputRef = useRef(null);
  
  // Use custom hook for search predictions
  const { predictions, showPredictions, setShowPredictions } = useSearchPredictions(searchTerm, hazards);
  
  // Update local searchTerm when activeFilters.searchTerm changes
  useEffect(() => {
    setSearchTerm(activeFilters.searchTerm || '');
  }, [activeFilters.searchTerm]);
  
  // Group risk ratings by their color - memoized
  const riskColorOptions = useMemo(() => {
    if (!riskMatrix?.colors) return [];
    
    const uniqueColors = new Map();
    
    // Group ratings by color
    Object.entries(riskMatrix.colors).forEach(([rating, color]) => {
      if (!uniqueColors.has(color)) {
        uniqueColors.set(color, {
          color,
          ratings: [rating]
        });
      } else {
        uniqueColors.get(color).ratings.push(rating);
      }
    });
    
    // Convert map to array
    return Array.from(uniqueColors.values());
  }, [riskMatrix]);
  
  // Filter options - memoized to prevent recreating on each render
  const filterOptions = useMemo(() => ({
    unmitigatedRisk: {
      title: "Unmitigated Risk",
      options: riskColorOptions
    },
    retainedRisk: {
      title: "Retained Risk",
      options: riskColorOptions
    },
    riskStatus: {
      title: "Risk Status",
      options: [
        "Open",
        "Managed",
        "Resolved",
        "For Transfer",
        "Transferred",
        "Closed",
        "Cancelled"
      ]
    },
    mitigationStatus: {
      title: "Mitigation Status",
      options: [
        "Proposed",
        "Implemented",
        "Transferred",
        "Rejected"
      ]
    }
  }), [riskColorOptions]);

  // Memoized calculation of whether there are active filters
  const hasActiveFilters = useMemo(() => 
    searchTerm || Object.values(activeFilters).some(v => Array.isArray(v) && v.length > 0),
  [searchTerm, activeFilters]);

  // Optimized handler functions with useCallback
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchTerm(value);
    onFilterChange({ 
      ...activeFilters, 
      searchTerm: value 
    });
  }, [activeFilters, onFilterChange]);
  
  const handleSearchKeyDown = useCallback((e) => {
    // Handle Enter key press to submit the search and hide predictions
    if (e.key === 'Enter') {
      e.preventDefault();
      setShowPredictions(false);
      
      // Apply the current search term
      onFilterChange({
        ...activeFilters,
        searchTerm: searchTerm
      });
      
      // Remove focus from the input
      if (searchInputRef.current) {
        searchInputRef.current.blur();
      }
    }
  }, [activeFilters, onFilterChange, searchTerm, setShowPredictions]);
  
  const handlePredictionSelect = useCallback((prediction) => {
    // Don't set the search term or filter, just highlight the result
    setShowPredictions(false);
    
    // Pass the prediction data to the parent for temporary highlighting
    onHighlightResult({
      id: prediction.id,
      property: prediction.property,
      term: searchTerm,
      text: prediction.text
    });
  }, [onHighlightResult, searchTerm, setShowPredictions]);

  const toggleRiskFilter = useCallback((key, colorOption) => {
    const currentValues = activeFilters[key] || [];
    
    // Check if any ratings from this color are already selected
    const hasSelectedRatings = colorOption.ratings.some(rating => 
      currentValues.includes(rating)
    );
    
    let newValues;
    if (hasSelectedRatings) {
      // Remove all ratings with this color
      newValues = currentValues.filter(value => 
        !colorOption.ratings.includes(value)
      );
    } else {
      // Add all ratings with this color
      newValues = [...currentValues, ...colorOption.ratings];
    }
    
    onFilterChange({
      ...activeFilters,
      [key]: newValues
    });
  }, [activeFilters, onFilterChange]);

  const toggleFilter = useCallback((key, value) => {
    const currentValues = activeFilters[key] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    
    onFilterChange({
      ...activeFilters,
      [key]: newValues
    });
  }, [activeFilters, onFilterChange]);

  const removeFilter = useCallback((key, value) => {
    const newValues = (activeFilters[key] || []).filter(v => v !== value);
    onFilterChange({
      ...activeFilters,
      [key]: newValues
    });
  }, [activeFilters, onFilterChange]);

  // Optimize calculation of whether a color is selected
  const isColorSelected = useCallback((key, colorOption) => {
    const currentValues = activeFilters[key] || [];
    if (colorOption.ratings.length === 0) return { allSelected: false, someSelected: false };
    
    // Check if all ratings for this color are selected
    const allSelected = colorOption.ratings.every(rating => 
      currentValues.includes(rating)
    );
    
    // Check if some ratings for this color are selected
    const someSelected = !allSelected && colorOption.ratings.some(rating => 
      currentValues.includes(rating)
    );
    
    return { allSelected, someSelected };
  }, [activeFilters]);

  // Function to get risk rating badge with appropriate color
  const getRiskBadge = useCallback((rating) => {
    const color = riskMatrix?.colors?.[rating];
    if (!color) return rating;
    
    return <RiskBadge rating={rating} color={color} />;
  }, [riskMatrix]);

  // Close predictions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.search-container')) {
        setShowPredictions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setShowPredictions]);

  // Toggle popover state handler
  const handleTogglePopover = useCallback((key, open) => {
    setOpenPopover(prev => ({ ...prev, [key]: open }));
  }, []);

  // Clear search term handler
  const handleClearSearchTerm = useCallback(() => {
    setSearchTerm('');
    onFilterChange({ ...activeFilters, searchTerm: '' });
  }, [activeFilters, onFilterChange]);

  return (
    <div className="bg-white pt-4 rounded-lg">
      <div className="flex flex-col space-y-3">
        {/* Search and Filters Row */}
        <div className="flex items-center space-x-2">
          {/* Search Input with Predictive Results */}
          <div className="relative w-full search-container">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              ref={searchInputRef}
              placeholder="Search hazards..."
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              className="pl-8"
              onFocus={() => {
                if (predictions.length > 0) {
                  setShowPredictions(true);
                }
              }}
            />
            
            {/* Predictive Search Results */}
            {showPredictions && (
              <div className="absolute z-[9999] mt-1 w-full bg-white shadow-lg rounded-md border border-gray-200 max-h-[300px] overflow-y-auto">
                {predictions.length === 0 ? (
                  <div className="p-2 text-sm text-gray-500">No matches found</div>
                ) : (
                  <ul className="py-1">
                    {predictions.map((prediction, index) => (
                      <li 
                        key={index}
                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                        onClick={() => handlePredictionSelect(prediction)}
                      >
                        <div className="flex flex-col">
                          <span className="font-semibold">
                            {prediction.id} - {prediction.property}
                          </span>
                          <span className="text-gray-700">
                            {prediction.context}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          {/* Filter Buttons */}
          <div className="flex items-center space-x-2">
            {Object.entries(filterOptions).map(([key, { title, options }]) => (
              <Popover
                key={key}
                open={openPopover[key]}
                onOpenChange={(open) => handleTogglePopover(key, open)}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      "border-dashed h-9",
                      (activeFilters[key]?.length ?? 0) > 0 && "border-solid border-primary"
                    )}
                  >
                    {title}
                    {(activeFilters[key]?.length ?? 0) > 0 && (
                      <Badge
                        variant="secondary"
                        className="ml-1 rounded-sm px-1 font-normal"
                      >
                        {activeFilters[key].length}
                      </Badge>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[200px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder={`Search ${title.toLowerCase()}...`} />
                    <CommandEmpty>No results found.</CommandEmpty>
                    <CommandGroup>
                      {key === 'unmitigatedRisk' || key === 'retainedRisk' ? (
                        // Render color blocks for risk ratings
                        options.map((colorOption) => {
                          const { allSelected, someSelected } = isColorSelected(key, colorOption);
                          return (
                            <CommandItem
                              key={colorOption.color}
                              onSelect={() => toggleRiskFilter(key, colorOption)}
                              className="flex items-center"
                            >
                              <div className={cn(
                                "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                allSelected
                                  ? "bg-primary text-primary-foreground"
                                  : someSelected
                                    ? "bg-primary/50 text-primary-foreground"
                                    : "opacity-50 [&_svg]:invisible"
                              )}>
                                <Check className={cn("h-4 w-4", (allSelected || someSelected) ? "opacity-100" : "opacity-0")} />
                              </div>
                              <div 
                                className="h-4 w-4 rounded-sm mr-2" 
                                style={{ backgroundColor: colorOption.color, border: '1px solid #ccc' }}
                              />
                              <span>
                                {colorOption.ratings.join(', ')}
                              </span>
                            </CommandItem>
                          );
                        })
                      ) : (
                        // Regular options
                        options.map((option) => {
                          const isSelected = (activeFilters[key] || []).includes(option);
                          return (
                            <CommandItem
                              key={option}
                              onSelect={() => toggleFilter(key, option)}
                            >
                              <div className={cn(
                                "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                isSelected
                                  ? "bg-primary text-primary-foreground"
                                  : "opacity-50 [&_svg]:invisible"
                              )}>
                                <Check className={cn("h-4 w-4", isSelected ? "opacity-100" : "opacity-0")} />
                              </div>
                              {option}
                            </CommandItem>
                          );
                        })
                      )}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>
            ))}

            {hasActiveFilters && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={onClearFilters}
                className="h-9"
              >
                Clear all
              </Button>
            )}
          </div>
        </div>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-1.5">
            {searchTerm && (
              <Badge variant="secondary" className="h-6 px-2 flex items-center gap-1">
                {searchTerm}
                <X 
                  className="h-3 w-3 text-muted-foreground hover:text-foreground cursor-pointer"
                  onClick={handleClearSearchTerm}
                />
              </Badge>
            )}
            {Object.entries(activeFilters).map(([key, values]) => {
              if (key === 'searchTerm' || !values?.length) return null;
              
              return values.map(value => (
                <Badge
                  key={`${key}-${value}`}
                  variant="secondary"
                  className="h-6 px-2 flex items-center gap-1"
                >
                  {(key === 'unmitigatedRisk' || key === 'retainedRisk') && riskMatrix?.colors?.[value] 
                    ? getRiskBadge(value)
                    : value}
                  <X 
                    className="h-3 w-3 text-muted-foreground hover:text-foreground cursor-pointer"
                    onClick={() => removeFilter(key, value)}
                  />
                </Badge>
              ));
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(HazardSearchFilter);